import { useRef } from 'react';
import { techOfferModal as content } from '~/content/tech';
import { ModalShell } from '~/components/layout';
import { ButtonLink, Icon } from '~/components/ui';
import { useContent } from '~/lib/useLanguage';

/**
 * "Learn more" dialog for the AI tools cashback offer (Figma 1382:9943), in the
 * shared QR-modal chrome (ModalShell: blur backdrop, rising card, cursor-following
 * close, Escape, scroll lock and focus handling) and the same card treatment as
 * MarketersOfferModal.
 */
export function TechOfferModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { ui } = useContent();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      labelledBy="tech-offer-title"
      initialFocusRef={closeButtonRef}
      className="w-full max-w-[750px]">
      {/* Tall content: the card scrolls inside itself rather than the page. */}
      <div className="flex max-h-[80dvh] flex-col overflow-y-auto overscroll-contain rounded-[32px] bg-white px-6 pt-6 pb-8 text-grey-1000 sm:max-h-[90dvh] md:px-8 md:pt-8">
        <button
          ref={closeButtonRef}
          type="button"
          aria-label={ui.close}
          onClick={onClose}
          className="flex size-6 cursor-pointer items-center justify-center self-end">
          <Icon name="close" />
        </button>

        <div className="mt-10 border-b border-black-6 pb-8">
          <h2
            id="tech-offer-title"
            className="max-w-[329px] text-h3 leading-[1.16] font-semibold tracking-[-0.704px] md:max-w-[593px] md:text-h2 md:tracking-[-0.88px]">
            {content.title}
          </h2>
        </div>
        <p className="mt-[23px] text-[15px] leading-[1.32] tracking-[0.27px] text-black md:max-w-[686px] md:text-body">
          {content.body}
        </p>

        <h3 className="mt-10 py-2 text-[15px] leading-[1.32] font-semibold tracking-[0.27px] md:text-body md:leading-[1.16]">
          {content.howItWorksTitle}
        </h3>
        <ol className="mt-[17px] flex flex-col gap-2 border-b border-black-6 pb-7">
          {content.steps.map((step) => (
            <li key={step.text} className="flex items-center gap-5">
              <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-black-6">
                <Icon name={step.icon} className="size-5" />
              </span>
              <span className="py-2 text-[15px] leading-[1.32] tracking-[0.27px] md:text-body">
                {step.text}
              </span>
            </li>
          ))}
        </ol>

        <p className="mt-10 text-[15px] leading-[1.32] tracking-[0.27px] md:text-body">
          <span className="block font-semibold">{content.closingTitle}</span>
          {content.closingBody}
        </p>
        {/* The CTA opens the app QR modal (or the store on mobile); close this one
            in the same click so the two dialogs never stack. */}
        <div className="mt-10 self-start" onClick={onClose}>
          <ButtonLink
            href={content.cta.href}
            className="max-md:!text-[14px] max-md:!tracking-[0.168px]">
            {content.cta.label}
          </ButtonLink>
        </div>
        <p className="mt-6 text-[14px] leading-[1.32] tracking-[0.252px] text-grey-500">
          {content.terms}
        </p>
      </div>
    </ModalShell>
  );
}
